/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, SxProps, Theme, Typography } from "@mui/material";
import { DataGridPremium, DataGridPremiumProps, GridValidRowModel } from "@mui/x-data-grid-premium";
import useMergedSx from "../../hooks/useMergedSx";
import CenteredWrapper from "../CenteredWrapper";
import ScrollableFlexContainer from "../ScrollableFlexContainer";
import InlineLoader from "../inlineLoader/InlineLoader";

interface CustomProps {
  containerSx?: SxProps<Theme>;
  scrollContainerSx?: SxProps<Theme>;
  selectableRows?: boolean;
  noScrollableContainer?: boolean;
  multilineRows?: boolean;
  noRowsText?: string;
  noRowsIcon?: React.ReactNode;
  /**
   * If `true`, the row action column is shifted to the left to prevent it from being overlapped by Intercom.
   */
  shiftActionsLeft?: boolean;
}

type Props<R extends GridValidRowModel = any> = CustomProps &
  DataGridPremiumProps<R> &
  React.RefAttributes<HTMLDivElement>;

const DataGrid = <RowModel extends GridValidRowModel = any>({
  containerSx,
  scrollContainerSx,
  selectableRows,
  noScrollableContainer,
  multilineRows,
  noRowsText,
  noRowsIcon,
  shiftActionsLeft,
  ...gridProps
}: Props<RowModel>) => {
  const { sx, slots, ...otherProps } = gridProps;

  const selectableRowsStyle = selectableRows
    ? {
        ".MuiDataGrid-row:not([data-id^='auto-generated-row-'])": {
          "&:hover": {
            cursor: "pointer",
          },
          ".MuiDataGrid-cell": {
            ":first-of-type": {
              cursor: "pointer",
            },
          },
          "&:hover .MuiDataGrid-cell:first-of-type": {
            color: "primary.main",
          },
        },
      }
    : undefined;

  const shiftActionsLeftStyle = shiftActionsLeft
    ? {
        ".MuiDataGrid-cell": {
          "&.grid-row-actions": {
            paddingRight: 2,
          },
        },
      }
    : undefined;

  const props = {
    rowHeight: multilineRows ? 52 : 40,
    columnHeaderHeight: 36,
    slots: {
      loadingOverlay: () => <InlineLoader />,
      noRowsOverlay: () => (
        <CenteredWrapper>
          <Stack spacing={2} alignItems="center">
            {noRowsIcon}
            <Typography color="text.secondary">{noRowsText ?? "No rows"}</Typography>
          </Stack>
        </CenteredWrapper>
      ),
      ...slots,
    },
    hideFooter: true,
    disableColumnFilter: true,
    disableColumnSelector: true,
    disableColumnMenu: true,
    disableRowSelectionOnClick: true,
    ...otherProps,
    sx: useMergedSx(sx, selectableRowsStyle, shiftActionsLeftStyle),
  };

  if (noScrollableContainer) {
    return <DataGridPremium<RowModel> {...props} />;
  }

  return (
    <ScrollableFlexContainer sx={{ height: "100%", ...containerSx }} scrollContainerSx={scrollContainerSx}>
      <DataGridPremium<RowModel> {...props} />
    </ScrollableFlexContainer>
  );
};

export default DataGrid;
